import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  TrendingUp, 
  Star, 
  ChevronRight, 
  Calculator, 
  Gift,
  Sparkles,
  ArrowUp,
  Info,
  Award,
  Coins,
  Users,
  Heart
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Tier icons and colors - Guardian theme
const TIER_CONFIG = {
  bronze: { icon: Shield, bgClass: 'from-amber-700 to-amber-900', textClass: 'text-amber-100' },
  silver: { icon: Shield, bgClass: 'from-slate-400 to-slate-600', textClass: 'text-white' },
  gold: { icon: Award, bgClass: 'from-yellow-400 to-amber-500', textClass: 'text-amber-900' },
  platinum: { icon: Sparkles, bgClass: 'from-slate-200 to-slate-400', textClass: 'text-slate-800' }
};

export default function ProtectionRewards({ compact = false }) {
  const [rewardsStatus, setRewardsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCalculator, setShowCalculator] = useState(false);
  const [calcAmount, setCalcAmount] = useState(100);
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);

  useEffect(() => {
    fetchRewardsStatus();
  }, []);

  const fetchRewardsStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE}/api/protection-rewards/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRewardsStatus(data);
      }
    } catch (error) {
      console.error('Error fetching rewards status:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateProjection = async () => {
    setCalcLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/protection-rewards/calculator?amount=${calcAmount}&months=12`
      );
      if (response.ok) {
        const data = await response.json();
        setCalcResult(data);
      }
    } catch (error) {
      console.error('Error calculating projection:', error);
    } finally {
      setCalcLoading(false);
    }
  };

  useEffect(() => {
    if (showCalculator) {
      calculateProjection();
    }
  }, [calcAmount, showCalculator]);

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-6">
          <div className="h-32 bg-stone-100 rounded-xl" />
        </CardContent>
      </Card>
    );
  }

  if (!rewardsStatus) {
    return (
      <Card className="overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Protect Your Club Members</h3>
              <p className="text-white/80 text-sm">Build trust & earn rewards!</p>
            </div>
          </div>
          <p className="text-sm text-white/70">
            Login to see your protection status. Your deposits safeguard fellow club members' contributions while earning interest.
          </p>
        </div>
      </Card>
    );
  }

  const { current_tier, total_deposits, monthly_earnings, yearly_earnings, next_tier, progress_to_next, amount_to_next_tier, club_deposits } = rewardsStatus;
  const TierIcon = TIER_CONFIG[current_tier.tier_id]?.icon || Shield;
  const tierConfig = TIER_CONFIG[current_tier.tier_id] || TIER_CONFIG.bronze;

  // Compact view for dashboard
  if (compact) {
    return (
      <Card className="overflow-hidden" data-testid="protection-rewards-compact">
        <div className={`bg-gradient-to-r ${tierConfig.bgClass} p-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <TierIcon className={`w-5 h-5 ${tierConfig.textClass}`} />
              </div>
              <div className={tierConfig.textClass}>
                <p className="text-xs opacity-80">Member Protection</p>
                <p className="font-bold">{current_tier.name}</p>
              </div>
            </div>
            <div className={`text-right ${tierConfig.textClass}`}>
              <p className="text-xs opacity-80">Monthly Rewards</p>
              <p className="font-bold text-lg">+${monthly_earnings.toFixed(2)}</p>
            </div>
          </div>
          
          {next_tier && (
            <div className="mt-3">
              <div className="flex justify-between text-xs mb-1">
                <span className={`${tierConfig.textClass} opacity-80`}>Upgrade to {next_tier.name}</span>
                <span className={tierConfig.textClass}>${total_deposits.toFixed(0)} / ${next_tier.min_deposit}</span>
              </div>
              <Progress value={progress_to_next} className="h-2 bg-white/20" />
            </div>
          )}
        </div>
        
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-stone-500">Protection Fund</p>
              <p className="font-bold text-lg text-stone-900">${total_deposits.toFixed(2)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-stone-500">Guardian Rate</p>
              <p className="font-bold text-lg text-green-600">{current_tier.annual_rate}% APY</p>
            </div>
          </div>
          <p className="text-xs text-stone-400 mt-2 flex items-center">
            <Heart className="w-3 h-3 mr-1 text-red-400" />
            Protecting your club members' deposits
          </p>
        </CardContent>
      </Card>
    );
  }

  // Full view
  return (
    <div className="space-y-6" data-testid="protection-rewards-full">
      {/* Hero Section */}
      <Card className="overflow-hidden">
        <div className={`bg-gradient-to-r ${tierConfig.bgClass} p-6`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <motion.div 
                className="p-4 bg-white/20 rounded-2xl"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <TierIcon className={`w-10 h-10 ${tierConfig.textClass}`} />
              </motion.div>
              <div className={tierConfig.textClass}>
                <Badge className="bg-white/20 text-inherit mb-2">
                  {current_tier.tier_id.toUpperCase()} GUARDIAN
                </Badge>
                <h2 className="text-2xl font-bold">{current_tier.name}</h2>
                <p className="text-sm opacity-80">Earning {current_tier.annual_rate}% APY while protecting members</p>
              </div>
            </div>
            
            <div className={`text-right ${tierConfig.textClass}`}>
              <p className="text-sm opacity-80">Protection Fund</p>
              <p className="text-3xl font-bold">${total_deposits.toFixed(2)}</p>
            </div>
          </div>
        </div>
        
        <CardContent className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-xl">
              <TrendingUp className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="text-xs text-stone-500">Monthly Rewards</p>
              <p className="text-xl font-bold text-green-600">+${monthly_earnings.toFixed(2)}</p>
            </div>
            <div className="text-center p-4 bg-indigo-50 rounded-xl">
              <Gift className="w-6 h-6 text-indigo-600 mx-auto mb-2" />
              <p className="text-xs text-stone-500">Yearly Rewards</p>
              <p className="text-xl font-bold text-indigo-600">+${yearly_earnings.toFixed(2)}</p>
            </div>
            <div className="text-center p-4 bg-amber-50 rounded-xl">
              <Coins className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <p className="text-xs text-stone-500">Guardian Rate</p>
              <p className="text-xl font-bold text-amber-600">{current_tier.annual_rate}%</p>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-xl">
              <Users className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <p className="text-xs text-stone-500">Clubs Protected</p>
              <p className="text-xl font-bold text-purple-600">{club_deposits?.length || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Progress to Next Tier */}
      {next_tier && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold text-stone-900">Become a Stronger Guardian!</h3>
                <p className="text-sm text-stone-500">
                  Add ${amount_to_next_tier.toFixed(2)} more to reach {next_tier.name} and protect more members
                </p>
              </div>
              <Badge className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                {next_tier.annual_rate}% APY
              </Badge>
            </div>
            
            <div className="relative">
              <Progress value={progress_to_next} className="h-4 bg-stone-100" />
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-stone-500">${total_deposits.toFixed(0)}</span>
                <span className="text-indigo-600 font-medium">${next_tier.min_deposit}</span>
              </div>
            </div>
            
            <div className="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl">
              <p className="text-sm font-medium text-indigo-900 mb-2">
                <Users className="w-4 h-4 inline mr-1" />
                {next_tier.name} Benefits:
              </p>
              <div className="flex flex-wrap gap-2">
                {next_tier.perks?.map((perk, i) => (
                  <Badge key={i} variant="secondary" className="bg-white/80">
                    {perk}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Club Deposits Breakdown */}
      {club_deposits && club_deposits.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <Shield className="w-5 h-5 mr-2 text-indigo-600" />
              Clubs You're Protecting
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {club_deposits.map((club, index) => (
                <motion.div
                  key={club.club_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="p-4 hover:bg-stone-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-stone-900">{club.club_name}</p>
                      <p className="text-sm text-stone-500 flex items-center">
                        <Heart className="w-3 h-3 mr-1 text-red-400" />
                        Protecting members: ${club.deposit_amount.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-600 font-medium">+${club.monthly_earnings.toFixed(2)}/mo</p>
                      <p className="text-xs text-stone-400">guardian reward</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Earnings Calculator */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center">
              <Calculator className="w-5 h-5 mr-2 text-amber-600" />
              Earnings Calculator
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowCalculator(!showCalculator)}
              data-testid="toggle-calculator"
            >
              {showCalculator ? 'Hide' : 'Calculate'}
              <ChevronRight className={`w-4 h-4 ml-1 transition-transform ${showCalculator ? 'rotate-90' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        
        <AnimatePresence>
          {showCalculator && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <CardContent className="pt-0">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium">Deposit Amount</label>
                      <span className="text-lg font-bold text-indigo-600">${calcAmount}</span>
                    </div>
                    <Slider
                      value={[calcAmount]}
                      onValueChange={(val) => setCalcAmount(val[0])}
                      min={10}
                      max={5000}
                      step={10}
                      className="mb-4"
                    />
                    <div className="flex justify-between text-xs text-stone-500">
                      <span>$10</span>
                      <span>$5,000</span>
                    </div>
                  </div>
                  
                  {calcResult && !calcLoading && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm text-stone-500">After 12 months</p>
                          <p className="text-2xl font-bold text-green-700">
                            ${calcResult.final_balance.toFixed(2)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-stone-500">Total Earned</p>
                          <p className="text-xl font-bold text-green-600">
                            +${calcResult.total_earned.toFixed(2)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm">
                        <Badge 
                          className="text-white"
                          style={{ backgroundColor: calcResult.tier?.color }}
                        >
                          {calcResult.tier?.name}
                        </Badge>
                        <span className="text-stone-600">
                          at {calcResult.annual_rate}% APY
                        </span>
                      </div>
                    </motion.div>
                  )}
                  
                  {calcLoading && (
                    <div className="p-4 bg-stone-50 rounded-xl text-center">
                      <div className="animate-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto" />
                    </div>
                  )}
                </div>
              </CardContent>
            </motion.div>
          )}
        </AnimatePresence>
      </Card>

      {/* Tier Perks */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center">
            <Star className="w-5 h-5 mr-2 text-yellow-500" />
            Your Guardian Benefits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {current_tier.perks?.map((perk, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl"
              >
                <div className="w-2 h-2 bg-amber-500 rounded-full" />
                <span className="text-sm font-medium text-stone-700">{perk}</span>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Info Section */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-none">
        <CardContent className="p-6">
          <div className="flex gap-4">
            <div className="p-3 bg-white rounded-xl shadow-sm">
              <Info className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h4 className="font-semibold text-stone-900 mb-1">How Member Protection Works</h4>
              <p className="text-sm text-stone-600">
                Your protection deposits create a safety fund that safeguards fellow club members' contributions. 
                If a member faces difficulties, your deposit helps ensure they don't lose their savings. 
                In return, you earn interest based on your Guardian tier. The more you protect, the higher your rewards!
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
